// Menu.jsx — навигация админпанели.
// Дизайн v2: тёмное стеклянное меню, SVG-иконки, активная подсветка
// жёлтой полоской слева + soft-фон, плавный hover.
//
// При свёрнутом меню иконки остаются, текст исчезает, ширина 64px.
// Логотип SK адаптируется к ширине меню.

import React from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import cl from './Menu.module.css';
import {
    IconDashboard, IconStudents, IconJournal, IconEvents, IconPays, IconSettings,
    IconAccounts, IconShieldCheck, IconChart, IconBook, IconUser, IconMenu,
} from '../Icons';

// Решает, должен ли пункт меню подсветиться для текущего URL.
// Каждому пункту задаём список префиксов, по которым он считается «своим».
// Это нужно, потому что:
//   - /main/jornal (Журнал) и /main/jornal/settings (Настройки) пересекаются
//     по префиксу, и без явных правил «Журнал» подсвечивался бы и на странице
//     настроек.
//   - страница курса лежит по /main/journal/course/:id (через английский «journal»),
//     а пункт меню — по /main/jornal (русская транслитерация). Без специального
//     правила пункт «Журнал» бы не подсвечивался при просмотре курса.
function isItemActive(itemTo, pathname) {
    const map = {
        '/main/students':          ['/main/students'],
        '/main/jornal':            ['/main/jornal', '/main/journal'],   // курс/занятие
        '/main/events':            ['/main/events'],
        '/main/pays':              ['/main/pays'],
        // Настройки журнала: и сама страница, и редактирование курсов/мероприятий
        '/main/jornal/settings':   ['/main/jornal/settings', '/main/edit-courses', '/main/edit-events'],
        '/main/accountStudents':   ['/main/accountStudents'],
        '/main/prepodandadmin':    ['/main/prepodandadmin'],
        '/main/progressstudent':   ['/main/progressstudent'],
        '/main/aproveproject':     ['/main/aproveproject'],
        '/main/materials':         ['/main/materials'],
        '/main/me':                ['/main/me'],
        '/main/settings':          ['/main/settings'],
    };
    const prefixes = map[itemTo] || [itemTo];
    // Исключаем пересечение «Журнал» с «Настройки журнала»: если текущий путь
    // начинается с /main/jornal/settings — это пункт «Настройки», не «Журнал».
    if (itemTo === '/main/jornal' && pathname.startsWith('/main/jornal/settings')) {
        return false;
    }
    return prefixes.some(p => pathname === p || pathname.startsWith(p + '/'));
}

const Menu = ({ isExpanded, onToggle, isMobile, onCloseMobile }) => {
    const navigate = useNavigate();
    const { pathname } = useLocation();
    // На мобиле меню — выезжающий drawer поверх контента.
    // Закрываем после перехода по любому пункту, чтобы освободить экран.
    const isMobileDrawerOpen = isMobile && isExpanded;

    const menuItems = [
        {
            section: 'Журнал',
            items: [
                { to: '/main/students', text: 'Ученики', Icon: IconStudents },
                { to: '/main/jornal', text: 'Журнал', Icon: IconJournal },
                { to: '/main/events', text: 'Мероприятия', Icon: IconEvents },
                { to: '/main/pays', text: 'Платежи', Icon: IconPays },
                { to: '/main/jornal/settings', text: 'Настройки', Icon: IconSettings },
            ],
        },
        {
            section: 'Аккаунты',
            items: [
                { to: '/main/accountStudents', text: 'Аккаунты учеников', Icon: IconAccounts },
                { to: '/main/prepodandadmin', text: 'Преподы и админы', Icon: IconUser },
            ],
        },
        {
            section: 'РобоТехника',
            items: [
                { to: '/main/progressstudent', text: 'Прогресс учеников', Icon: IconChart },
                { to: '/main/aproveproject', text: 'Проверка проектов', Icon: IconShieldCheck },
            ],
        },
        {
            section: 'Материалы',
            items: [
                { to: '/main/materials', text: 'Статьи и материалы', Icon: IconBook },
            ],
        },
        {
            section: 'Профиль',
            items: [
                { to: '/main/me', text: 'Мой аккаунт', Icon: IconUser },
                { to: '/main/settings', text: 'Настройки', Icon: IconSettings },
            ],
        },
    ];

    const handleLinkClick = () => {
        if (isMobile && onCloseMobile) onCloseMobile();
    };

    return (
        <>
            {/* Затемнение под выезжающим меню на мобиле */}
            {isMobileDrawerOpen && (
                <div
                    className={cl.drawerOverlay}
                    onClick={onCloseMobile}
                    aria-hidden="true"
                />
            )}

            <aside
                className={[
                    cl.menu,
                    isMobile ? cl.mobile : '',
                    isExpanded ? cl.expanded : cl.collapsed,
                    isMobileDrawerOpen ? cl.drawerOpen : '',
                ].filter(Boolean).join(' ')}
            >
                {/* Логотип */}
                <button
                    className={cl.brand}
                    onClick={() => {
                        navigate('/main');
                        handleLinkClick();
                    }}
                    aria-label="На главную"
                >
                    <span className={cl.brandLogo}>SK</span>
                    {isExpanded && <span className={cl.brandName}>СтартКласс</span>}
                </button>

            {/* Главный пункт (отдельно) — Дашборд */}
            <nav className={cl.navTop}>
                <NavLink
                    end
                    to="/main"
                    onClick={handleLinkClick}
                    className={({ isActive }) => `${cl.link} ${cl.linkPrimary} ${isActive ? cl.active : ''}`}
                >
                    <span className={cl.linkIcon}><IconDashboard width={20} height={20}/></span>
                    {isExpanded && <span className={cl.linkText}>Дашборд</span>}
                    <span className={cl.linkIndicator}/>
                </NavLink>
            </nav>

            {/* Разделы меню */}
            <nav className={`${cl.nav} sk-scrollbar`}>
                {menuItems.map((section) => (
                    <div className={cl.section} key={section.section}>
                        {isExpanded && (
                            <div className={cl.sectionTitle}>{section.section}</div>
                        )}
                        {section.items.map((item) => {
                            const active = isItemActive(item.to, pathname);
                            return (
                                <NavLink
                                    key={item.to}
                                    to={item.to}
                                    onClick={handleLinkClick}
                                    className={`${cl.link} ${active ? cl.active : ''}`}
                                    title={!isExpanded ? item.text : undefined}
                                >
                                    <span className={cl.linkIcon}>
                                        <item.Icon width={20} height={20}/>
                                    </span>
                                    {isExpanded && <span className={cl.linkText}>{item.text}</span>}
                                    <span className={cl.linkIndicator}/>
                                </NavLink>
                            );
                        })}
                    </div>
                ))}
            </nav>

            {/* Кнопка свернуть/развернуть — только на десктопе */}
            {!isMobile && (
                <button
                    className={cl.toggle}
                    onClick={onToggle}
                    aria-label={isExpanded ? 'Свернуть меню' : 'Развернуть меню'}
                >
                    <IconMenu width={18} height={18}/>
                </button>
            )}
        </aside>
        </>
    );
};

export default Menu;
